import { ApiError, type components } from "@brandex/api-client";
import type { MessageKey } from "@brandex/i18n";
import { Alert, Button, ChoiceChips, Field, Input, Progress, Spinner } from "@brandex/ui";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useBrandex, useT } from "../context";
import { errorField, errorMessage } from "../errors";
import { usePublicPlans } from "../queries";
import { STEPS, draftField, emptyDraft, stepForField, toRequest, validateStep, type OnboardingDraft, type Step, type StepErrors } from "./draft";
import { BrandStep, ChambersStep, PackageStep, PracticeStep, QualificationsStep } from "./steps";

export type OnboardedClient = components["schemas"]["OnboardedClientDto"];
type InviteLocale = "en" | "bn";

/**
 * Staff onboard a client on their behalf (F-17): the wizard's steps without
 * a password. The doctor gets an email to choose one. Used by the admin app.
 */
export function ClientOnboardingForm({ onDone }: { onDone?: (client: OnboardedClient, email: string) => void }) {
  const { api } = useBrandex();
  const t = useT();
  const plans = usePublicPlans();
  const [draft, setDraft] = useState<OnboardingDraft>(emptyDraft);
  const [inviteLocale, setInviteLocale] = useState<InviteLocale>("bn");
  const [step, setStep] = useState<Step>("account");
  const [errors, setErrors] = useState<StepErrors>({});
  const heading = useRef<HTMLHeadingElement>(null);
  const first = useRef(true);
  useEffect(() => {
    // Focus follows step changes, not the first render.
    if (first.current) first.current = false;
    else heading.current?.focus();
  }, [step]);

  const update = (patch: Partial<OnboardingDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const create = useMutation({
    mutationFn: async () => {
      const { data } = await api.http.POST("/v1/admin/clients", {
        body: {
          email: draft.email.trim(),
          phone: draft.phone.trim(),
          locale: inviteLocale,
          profile: toRequest(draft, plans.data ?? []),
        },
      });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
    onSuccess: (data) => onDone?.(data, draft.email.trim()),
    onError: (e) => {
      const raw = errorField(e);
      // Profile fields come back under the nested `profile` object.
      const field = raw?.replace(/^profile\./, "");
      const target = e instanceof ApiError && e.code === "email_taken" ? "account" : stepForField(field);
      if (target !== null) setStep(target);
      if (e instanceof ApiError && e.code === "email_taken") setErrors({ email: "error.email_taken" });
      else if (field !== undefined) setErrors({ [draftField(field)]: "onboarding.error.server" });
    },
  });

  const index = STEPS.indexOf(step);
  const next = () => {
    const e = validateStep(step, draft, null);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    if (step === "brand") create.mutate();
    else setStep(STEPS[index + 1] ?? step);
  };
  const back = () => {
    setErrors({});
    const prev = STEPS[index - 1];
    if (prev !== undefined) setStep(prev);
  };
  const err = (field: string) => (errors[field] === undefined ? undefined : t(errors[field]));
  const serverError = create.error !== null && (step === "brand" || stepForField(errorField(create.error)?.replace(/^profile\./, "")) === step) ? create.error : null;

  return (
    <div className="grid gap-5">
      <div className="flex items-center justify-between gap-3">
        <Progress value={((index + 1) / STEPS.length) * 100} label={t("clientOnboarding.title")} />
        <span className="shrink-0 text-sm text-muted-foreground">{t("onboarding.step", { n: index + 1, total: STEPS.length })}</span>
      </div>
      <form
        noValidate
        className="glass grid gap-6 rounded-[var(--radius-xl)] p-6 md:p-8"
        onSubmit={(e) => {
          e.preventDefault();
          next();
        }}
      >
        <h2 ref={heading} tabIndex={-1} className="font-display text-xl font-bold tracking-tight outline-none">
          {step === "account" ? t("clientOnboarding.account") : t(`onboarding.steps.${step}` as MessageKey)}
        </h2>
        {serverError !== null && !(serverError instanceof ApiError && serverError.code === "email_taken") ? (
          <Alert tone="error">
            {serverError instanceof ApiError && serverError.code === "validation_failed" && serverError.field !== undefined
              ? t("onboarding.error.serverDetail", { detail: serverError.message })
              : errorMessage(serverError, t)}
          </Alert>
        ) : null}

        {step === "account" ? (
          <div className="grid gap-4">
            <Field label={t("onboarding.fullName")} hint={t("onboarding.fullNameHint")} error={err("full_name")}>
              <Input autoComplete="off" maxLength={100} value={draft.fullName} onChange={(e) => update({ fullName: e.target.value })} />
            </Field>
            <Field label={t("auth.email")} error={err("email")}>
              <Input type="email" autoComplete="off" value={draft.email} onChange={(e) => update({ email: e.target.value })} />
            </Field>
            <Field label={t("onboarding.phone")} hint={t("onboarding.phoneHint")} error={err("phone")}>
              <Input type="tel" autoComplete="off" inputMode="tel" value={draft.phone} onChange={(e) => update({ phone: e.target.value })} />
            </Field>
            <div className="grid gap-2">
              <span className="text-sm font-medium leading-none">{t("clientOnboarding.inviteLanguage")}</span>
              <ChoiceChips
                label={t("clientOnboarding.inviteLanguage")}
                options={[
                  { value: "bn", label: t("language.bangla") },
                  { value: "en", label: t("language.english") },
                ]}
                value={inviteLocale}
                onChange={(v) => setInviteLocale(v)}
              />
            </div>
          </div>
        ) : null}
        {step === "practice" ? <PracticeStep draft={draft} update={update} errors={errors} askName /> : null}
        {step === "qualifications" ? <QualificationsStep draft={draft} update={update} errors={errors} /> : null}
        {step === "chambers" ? <ChambersStep draft={draft} update={update} errors={errors} /> : null}
        {step === "package" ? (
          plans.isPending ? <Spinner label={t("common.loading")} /> : <PackageStep draft={draft} update={update} errors={errors} plans={plans.data ?? []} />
        ) : null}
        {step === "brand" ? <BrandStep draft={draft} update={update} errors={errors} /> : null}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
          {index > 0 ? (
            <Button variant="ghost" onClick={back}>
              <ArrowLeft className="h-4 w-4" aria-hidden />
              {t("onboarding.back")}
            </Button>
          ) : (
            <span />
          )}
          <Button type="submit" variant={step === "brand" ? "accent" : "primary"} loading={create.isPending}>
            {step === "brand" ? <Check className="h-4 w-4" aria-hidden /> : null}
            {t(step === "brand" ? "clientOnboarding.submit" : "onboarding.continue")}
            {step === "brand" ? null : <ArrowRight className="h-4 w-4" aria-hidden />}
          </Button>
        </div>
      </form>
    </div>
  );
}
